import React from "react";
import { useMeshStatus } from "@/hooks/useMeshStatus";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useConnectivity } from "@/hooks/useConnectivity";
import { toast } from "sonner";
import { isInstalledAppEnvironment } from "@/lib/device";
import { Radio, Cloud, CloudOff, Wifi, RefreshCw, User, ChefHat, Wine, ClipboardList, Shield, Crown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * MeshStatusPill — always-visible LAN mesh indicator.
 *
 * Header behaviour (Session 27.73):
 *   • Shows the LIVE count of devices on the mesh (self + peers).
 *   • Click / tap the pill to open a popover listing every device
 *     with its role and connection health.
 *   • Auto-refresh: pushed via `subscribeMeshStatus` (no polling
 *     interval needed — the mesh emits status changes on peer
 *     join, leave, handshake complete, and probe failure).
 *
 * States:
 *   LAN     → green pill "LAN · N devices" (fastest, one or more
 *             peer WebRTC data channels open)
 *   Cloud   → amber pill "Cloud" (signalling only, no peer channels)
 *   Offline → red pill   "Offline" (no network at all)
 *
 * The popover always shows THIS device first (labelled "This device"),
 * followed by every announced peer. "Announced" peers are ones the
 * signalling server told us about but whose WebRTC handshake hasn't
 * completed yet — surfacing them separately turns a mysterious
 * "we say Cloud even though there are two tablets in the room" into
 * "we see 2 devices, WebRTC handshakes still pending on both". That
 * is far easier for a manager to act on ("check Wi-Fi", "restart the
 * app on the coordinator device") than a blank Cloud pill.
 */

const ROLE_META = {
  owner:   { icon: Shield,         label: "Owner"   },
  manager: { icon: Shield,         label: "Manager" },
  admin:   { icon: Shield,         label: "Admin"   },
  staff:   { icon: ClipboardList,  label: "Staff"   },
  waiter:  { icon: ClipboardList,  label: "Waiter"  },
  kitchen: { icon: ChefHat,        label: "Kitchen" },
  bar:     { icon: Wine,           label: "Bar"     },
};

function roleMetaFor(role) {
  const key = String(role || "").toLowerCase();
  return ROLE_META[key] || { icon: User, label: key ? key.charAt(0).toUpperCase() + key.slice(1) : "Device" };
}

function truncateId(id, n = 8) {
  if (!id) return "";
  return id.length > n ? `${id.slice(0, n)}…` : id;
}

function DeviceRow({ label, role, ready, isSelf, subLabel, isCoordinator }) {
  const meta = roleMetaFor(role);
  const Icon = meta.icon;
  return (
    <div
      className={`flex items-center gap-2.5 rounded-lg border px-2.5 py-2 ${isCoordinator ? "border-amber-300 bg-amber-50/60 dark:border-amber-700/60 dark:bg-amber-950/30" : "border-border/50 bg-secondary/40"}`}
      data-testid={`mesh-device-row${isSelf ? "-self" : ""}${isCoordinator ? "-coordinator" : ""}`}
    >
      <div className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${ready ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-amber-500/15 text-amber-700 dark:text-amber-300"}`}>
        <Icon className="h-4 w-4" aria-hidden="true" />
        {isCoordinator ? (
          <span
            className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-amber-950 shadow-sm ring-1 ring-white dark:ring-slate-900"
            title="Today's Coordinator"
          >
            <Crown className="h-2.5 w-2.5" aria-hidden="true" />
          </span>
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="truncate text-xs font-semibold" title={label}>
            {label || "Unnamed device"}
          </p>
          {isSelf ? (
            <span
              className="inline-flex shrink-0 items-center rounded-full border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 px-1.5 py-[1px] text-[10px] font-semibold uppercase leading-none tracking-wide text-[hsl(var(--accent))]"
              data-testid="mesh-device-self-badge"
            >
              This device
            </span>
          ) : null}
          {isCoordinator ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-400/50 bg-amber-100 px-1.5 py-[1px] text-[10px] font-semibold uppercase leading-none tracking-wide text-amber-900 dark:border-amber-500/50 dark:bg-amber-500/20 dark:text-amber-200"
              data-testid="mesh-device-coordinator-badge"
            >
              <Crown className="h-2.5 w-2.5" aria-hidden="true" />
              Coordinator
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
          {meta.label}
          {subLabel ? ` · ${subLabel}` : ""}
        </p>
      </div>
      <span
        className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${ready ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-amber-500/15 text-amber-700 dark:text-amber-300"}`}
      >
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${ready ? "bg-emerald-500 dark:bg-emerald-400" : "bg-amber-500 dark:bg-amber-400"}`} aria-hidden="true" />
        {ready ? "LAN" : "Pairing…"}
      </span>
    </div>
  );
}

export function MeshStatusPill({ className = "" }) {
  const mesh = useMeshStatus();
  const net = useOnlineStatus();
  // Session 30.8 — tri-state connectivity (LAN vs internet vs Wi-Fi).
  const conn = useConnectivity();

  // Session 30 — The LAN / Cloud connectivity pill is only meaningful
  // inside the installed apps (Capacitor APK / Tauri EXE) where the
  // LAN mesh actually runs. On the regular web view it added visual
  // noise ("Cloud" forever), so it renders nothing there. NOTE: the
  // actual `return null` lives below the hooks to respect the Rules
  // of Hooks.
  const isNativeApp = React.useMemo(() => isInstalledAppEnvironment(), []);

  // Session 27.76 — Cross-reference the mesh peer list against the
  // current Coordinator's device_id so we can render a crown badge
  // on whichever row (self or peer) holds the role today. The App
  // publishes a getter on `window` because the coordinator state
  // lives in the CoordinatorElectionGate component tree, not in the
  // mesh store.
  const [coordinatorId, setCoordinatorId] = React.useState(null);
  React.useEffect(() => {
    const readId = () => {
      try {
        if (typeof window === "undefined") return null;
        if (typeof window.__nfcsGetCoordinatorDeviceId !== "function") return null;
        return window.__nfcsGetCoordinatorDeviceId();
      } catch (_) { return null; }
    };
    setCoordinatorId(readId());
    const t = setInterval(() => setCoordinatorId(readId()), 3000);
    return () => clearInterval(t);
  }, []);

  // Session 27.77 — Cached LAN peers ("devices we've seen on the LAN
  // in the last 7 days"). Refreshed every 5 seconds so the popover
  // stays live if new mDNS resolutions land while it's open.
  const [cachedPeers, setCachedPeers] = React.useState([]);
  React.useEffect(() => {
    const readCache = async () => {
      try {
        const m = await import("@/lib/lanPeerCache");
        setCachedPeers(m.listCachedLanPeers());
      } catch (_) { setCachedPeers([]); }
    };
    readCache();
    const t = setInterval(readCache, 5000);
    return () => clearInterval(t);
  }, []);

  // ─── Compute pill variant + label ──────────────────────────
  // Self counts as 1 in the total headline count so "3 devices"
  // means "3 tablets/PCs on this network", including the one the
  // operator is holding.
  const selfLabel = mesh.self?.device_label || "This device";
  const readyPeers = Array.isArray(mesh.peers) ? mesh.peers : [];
  const announcedPeers = Array.isArray(mesh.announcedPeers) ? mesh.announcedPeers : readyPeers;
  const totalDevices = 1 + announcedPeers.length; // self + every announced peer
  const readyDevices = 1 + readyPeers.length;

  let variant;
  let icon;
  let label;

  if (!net.online) {
    variant = "offline";
    icon = <CloudOff className="h-3.5 w-3.5" />;
    label = "Offline";
  } else if (mesh.status === "lan") {
    variant = "lan";
    icon = <Radio className="h-3.5 w-3.5" />;
    // Session 30.8 — Internet down ≠ LAN down. Keep the green LAN pill
    // and simply flag the missing internet.
    label = conn.internet === "down"
      ? `LAN · ${totalDevices} ${totalDevices === 1 ? "device" : "devices"} · no internet`
      : `LAN · ${totalDevices} ${totalDevices === 1 ? "device" : "devices"}`;
  } else if (mesh.lanLost) {
    variant = "offline";
    icon = <Radio className="h-3.5 w-3.5" />;
    label = "LAN lost";
  } else if (mesh.status === "cloud") {
    variant = "cloud";
    icon = <Cloud className="h-3.5 w-3.5" />;
    // Even in cloud mode, show the count so operators can see peers
    // being discovered even while WebRTC pairing is still in flight.
    label = announcedPeers.length > 0
      ? `Cloud · ${totalDevices} ${totalDevices === 1 ? "device" : "devices"}`
      : "Cloud";
  } else {
    variant = "cloud";
    icon = <Wifi className="h-3.5 w-3.5" />;
    label = "Cloud";
  }

  const styles = {
    lan:     "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-950/70",
    cloud:   "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/70",
    offline: "border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 dark:border-rose-700/60 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-950/70",
  };

  // ─── Controlled reconnect (Session 30.8) ────────────────────
  // The only UI path that may rebuild the mesh. `reconnectMesh`
  // refuses to tear down a healthy LAN mesh, so an accidental tap can
  // never break an established handshake.
  const lanHealthy = mesh.status === "lan" && (mesh.peerCount || 0) > 0;
  const handleRescan = () => {
    try {
      import("@/lib/lanMesh").then((mod) => {
        let r = { rebuilt: false };
        try { r = mod.reconnectMesh(); } catch (_) { /* noop */ }
        if (r.rebuilt) toast.message("Reconnecting to devices on this Wi-Fi…");
        else toast.message("LAN is healthy — nothing to reconnect.");
      });
      if (typeof window !== "undefined" && typeof window.__nfcsRescanCoordinator === "function") {
        window.__nfcsRescanCoordinator();
      }
    } catch (_) { /* noop */ }
  };

  // Web view (regular browser) → no pill at all.
  if (!isNativeApp) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="mesh-status-pill"
          data-variant={variant}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]/40 ${styles[variant]} ${className}`}
          aria-label={`Mesh status: ${label}. Tap to see connected devices.`}
        >
          {icon}
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-80 p-3"
        data-testid="mesh-status-popover"
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">Devices on this restaurant</p>
              <p className="text-[11px] text-muted-foreground" data-testid="mesh-status-summary">
                {!net.online
                  ? "This device is offline — no network detected."
                  : mesh.status === "lan"
                    ? `${readyDevices} of ${totalDevices} paired directly over Wi-Fi.${conn.internet === "down" ? " Internet is down — LAN keeps working, cloud sync resumes automatically." : ""}`
                    : mesh.lanLost
                      ? "LAN connection lost — devices were paired earlier. Use Reconnect LAN."
                    : announcedPeers.length > 0
                      ? `${totalDevices} devices detected · Wi-Fi pairing in progress.`
                      : "Cloud-only mode · no other devices online yet."}
              </p>
            </div>
            {lanHealthy ? null : (
              <button
                type="button"
                onClick={handleRescan}
                className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-border bg-secondary px-2 text-[10px] font-semibold uppercase tracking-wide hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]/40"
                data-testid="mesh-status-rescan"
                title="Controlled reconnect: rebuild the LAN mesh and re-check for the day's Coordinator."
              >
                <RefreshCw className="h-3 w-3" aria-hidden="true" />
                {mesh.lanLost ? "Reconnect LAN" : "Rescan"}
              </button>
            )}
          </div>

          <div className="space-y-1.5" data-testid="mesh-status-device-list">
            {/* Self first */}
            <DeviceRow
              label={selfLabel}
              role={mesh.self?.role}
              ready
              isSelf
              subLabel={truncateId(mesh.self?.peer_id)}
              isCoordinator={!!(coordinatorId && mesh.self?.peer_id && coordinatorId === mesh.self.peer_id)}
            />

            {/* Every announced peer — ready first, then pairing */}
            {announcedPeers.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border/60 bg-secondary/20 px-2.5 py-3 text-center text-[11px] text-muted-foreground">
                No other devices yet.<br />
                <span className="text-[10px]">
                  Open the app on another tablet on the same Wi-Fi to pair.
                </span>
              </p>
            ) : (
              [...announcedPeers]
                .sort((a, b) => (a.ready === b.ready ? 0 : a.ready ? -1 : 1))
                .map((p) => (
                  <DeviceRow
                    key={p.peer_id}
                    label={p.device_label || `Device · ${truncateId(p.peer_id)}`}
                    role={p.role}
                    ready={!!p.ready}
                    isSelf={false}
                    subLabel={truncateId(p.peer_id)}
                    isCoordinator={!!(coordinatorId && p.peer_id && coordinatorId === p.peer_id)}
                  />
                ))
            )}
          </div>

          {/* Session 27.77 — "Known LAN devices" pane. Lists every
              device we've seen on the LAN in the last 7 days (from
              WebRTC ICE candidates + mDNS discoveries). Devices that
              are NOT in the current live mesh but ARE in the cache
              are shown greyed out — useful for spotting "the kitchen
              tablet was here yesterday but hasn't come online today". */}
          {cachedPeers.length > 0 ? (
            <details className="rounded-lg border border-border/40 bg-secondary/20 open:bg-secondary/30" data-testid="mesh-known-devices-section">
              <summary className="flex cursor-pointer items-center justify-between gap-2 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
                <span>Known LAN devices ({cachedPeers.length})</span>
                <span className="text-[9px] font-normal normal-case">Seen in last 7 days</span>
              </summary>
              <div className="space-y-1 border-t border-border/40 px-2 py-2">
                {cachedPeers.map((p) => {
                  const lastSeen = p.last_seen_at ? new Date(p.last_seen_at) : null;
                  const ageMs = lastSeen ? Date.now() - lastSeen.getTime() : 0;
                  const ageStr = ageMs < 60_000 ? "just now"
                    : ageMs < 3_600_000 ? `${Math.round(ageMs / 60_000)}m ago`
                    : ageMs < 86_400_000 ? `${Math.round(ageMs / 3_600_000)}h ago`
                    : `${Math.round(ageMs / 86_400_000)}d ago`;
                  return (
                    <div
                      key={p.device_id}
                      className="flex items-center justify-between gap-2 rounded-md px-1.5 py-1 text-[11px]"
                      data-testid={`mesh-known-device-${p.device_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{p.device_label || `Device · ${p.device_id.slice(0, 8)}`}</p>
                        <p className="truncate text-[10px] text-muted-foreground">
                          {p.role || "unknown"}
                          {p.ipv4 ? ` · ${p.ipv4}` : ""}
                          {` · via ${p.source}`}
                        </p>
                      </div>
                      <span className="shrink-0 text-[10px] text-muted-foreground">{ageStr}</span>
                    </div>
                  );
                })}
              </div>
            </details>
          ) : null}

          <p className="text-[10px] leading-relaxed text-muted-foreground">
            {mesh.status === "lan"
              ? "Orders and \u201cready\u201d pings travel over Wi-Fi directly between paired devices — the cloud is only used as a backup."
              : "Devices are talking through the cloud right now. Pairing continues automatically; use Rescan to force a fresh handshake."}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default MeshStatusPill;
